import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LayoutGrid, FolderKanban, FileText, MessageSquare, LogOut, ArrowUpRight, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Overview } from "../components/dashboard/Overview";
import { Projects } from "../components/dashboard/Projects";
import { Quotes } from "../components/dashboard/Quotes";
import { Chat } from "../components/dashboard/Chat";
import { useHeartbeat } from "../hooks/useHeartbeat";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  useHeartbeat();

  const TABS = [
    { id: "overview", label: t("dash.tab_overview"), icon: LayoutGrid },
    { id: "projects", label: t("dash.tab_projects"), icon: FolderKanban },
    { id: "quotes", label: t("dash.tab_quotes"), icon: FileText },
    { id: "chat", label: t("dash.tab_chat"), icon: MessageSquare },
  ];

  useEffect(() => {
    const tt = params.get("tab");
    if (tt && tt !== tab) setTab(tt);
  }, [params, tab]);

  const switchTab = (id) => {
    setTab(id);
    setParams({ tab: id });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col lg:flex-row">
      <aside data-testid="dashboard-sidebar" className="lg:w-72 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 flex lg:flex-col bg-zinc-950">
        <div className="p-6 lg:p-8 hidden lg:block">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <span className="block w-2.5 h-2.5 bg-white" />
            <span className="font-display text-xl">Versant<span className="text-zinc-500">.</span>Studio</span>
          </Link>

          <div className="mb-10 pb-6 border-b border-white/10">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("dash.connected_as")}</p>
            <p className="text-sm font-medium text-zinc-100 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            {user && !user.email_verified && (
              <button
                onClick={() => navigate("/verify-email")}
                data-testid="verify-banner"
                className="mt-3 w-full text-left text-[11px] flex items-center gap-2 text-amber-300 hover:text-amber-200 border border-amber-500/30 px-2.5 py-2"
              >
                <Mail size={12} /> {t("verify.banner_unverified")}
              </button>
            )}
          </div>

          <nav className="space-y-1">
            {TABS.map((tt) => {
              const Icon = tt.icon;
              const active = tab === tt.id;
              return (
                <button
                  key={tt.id}
                  data-testid={`sidebar-tab-${tt.id}`}
                  onClick={() => switchTab(tt.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    active ? "bg-white text-zinc-950" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  {tt.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="lg:hidden flex items-center gap-1 overflow-x-auto p-3 w-full">
          {TABS.map((tt) => {
            const active = tab === tt.id;
            return (
              <button
                key={tt.id}
                data-testid={`mobile-tab-${tt.id}`}
                onClick={() => switchTab(tt.id)}
                className={`flex-shrink-0 px-4 py-2 text-xs uppercase tracking-[0.15em] ${
                  active ? "bg-white text-zinc-950" : "text-zinc-400 border border-white/10"
                }`}
              >
                {tt.label}
              </button>
            );
          })}
        </div>

        <div className="hidden lg:block mt-auto p-6 border-t border-white/10 space-y-2">
          <Link
            to="/"
            data-testid="dashboard-back-home"
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
          >
            <ArrowUpRight size={12} /> {t("common.back_home")}
          </Link>
          <button
            data-testid="dashboard-logout"
            onClick={async () => {
              navigate("/", { replace: true });
              await logout();
            }}
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
          >
            <LogOut size={12} /> {t("common.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen">
        <div className="border-b border-white/10 px-6 sm:px-12 py-8 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("dash.section")}</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">
              {TABS.find((tt) => tt.id === tab)?.label}
            </h1>
          </div>
        </div>

        <div className="px-6 sm:px-12 py-10">
          {tab === "overview" && <Overview onSwitch={switchTab} />}
          {tab === "projects" && <Projects />}
          {tab === "quotes" && <Quotes />}
          {tab === "chat" && <Chat />}
        </div>
      </main>
    </div>
  );
}
